import React, { useContext } from 'react'

import { useSelector } from 'react-redux'
import Flex from 'styled-flex-component'

import AnalyticsPostCampaignReportEmpty from '@components/Analytics/PostCampaignReport/ListView/Empty/Empty'
import AnalyticsPostCampaignReportListViewInsights from '@components/Analytics/PostCampaignReport/ListView/Insights/Insights'
import AnalyticsPostCampaignReportListViewAmount from '@components/Analytics/PostCampaignReport/ListView/Item/Amount'
import AnalyticsPostCampaignReportListViewNumber from '@components/Analytics/PostCampaignReport/ListView/Item/Number'
import AnalyticsPostCampaignReportListViewPercentage from '@components/Analytics/PostCampaignReport/ListView/Item/Percentage'
import {
  Entry,
  RowWrapper,
} from '@components/Analytics/PostCampaignReport/ListView/ListView.styled'
import { CampaignContext } from '@context/Campaign'
import { IApplicationState } from '@store'
import {
  selectPostComments,
  selectPostCPE,
  selectPostCPM,
  selectPostEngagement,
  selectPostEngagementPct,
  selectPostFollowers,
  selectPostImpressions,
  selectPostLikes,
  selectPostPublishedDate,
  selectPostReach,
  selectPostReachPct,
  selectPostSpend,
  selectShowPostPerformance,
} from '@store/postCampaignReport'
import Theme from '@theme'
import { Container, Text } from '@tribegroup'
import { abbreviateNumber } from '@utils'

interface IProps {
  submissionId: number
  backgroundColor: string
}

const AnalyticsPostCampaignReportPostListItem: React.FC<IProps> = ({
  submissionId,
  backgroundColor,
}) => {
  const {
    campaign: { is_price_hidden: isPriceHidden },
  } = useContext(CampaignContext)

  const publishedDate = useSelector((state: IApplicationState) =>
    selectPostPublishedDate(state, submissionId),
  )
  const spend = useSelector((state: IApplicationState) => selectPostSpend(state, submissionId))
  const followers = useSelector((state: IApplicationState) =>
    selectPostFollowers(state, submissionId),
  )
  const likes = useSelector((state: IApplicationState) => selectPostLikes(state, submissionId))
  const comments = useSelector((state: IApplicationState) =>
    selectPostComments(state, submissionId),
  )
  const engagement = useSelector((state: IApplicationState) =>
    selectPostEngagement(state, submissionId),
  )
  const engagementPct = useSelector((state: IApplicationState) =>
    selectPostEngagementPct(state, submissionId),
  )
  const cpe = useSelector((state: IApplicationState) => selectPostCPE(state, submissionId))
  const reach = useSelector((state: IApplicationState) => selectPostReach(state, submissionId))
  const reachPct = useSelector((state: IApplicationState) =>
    selectPostReachPct(state, submissionId),
  )
  const impressions = useSelector((state: IApplicationState) =>
    selectPostImpressions(state, submissionId),
  )
  const cpm = useSelector((state: IApplicationState) => selectPostCPM(state, submissionId))
  const showMetrics = useSelector((state: IApplicationState) =>
    selectShowPostPerformance(state, submissionId),
  )

  if (!showMetrics) {
    return (
      <Container data-testid="post-purchase-empty">
        <AnalyticsPostCampaignReportEmpty
          minWidth={isPriceHidden ? 58.5 : 78.375}
          backgroundColor={backgroundColor}
        />
      </Container>
    )
  }

  return (
    <Container inline relative height={4.75}>
      <Flex>
        <RowWrapper data-testid="post-published-date" backgroundColor={backgroundColor}>
          <Entry>
            <Text color={Theme.grey900Color}>{publishedDate}</Text>
          </Entry>
        </RowWrapper>
        {!isPriceHidden && (
          <AnalyticsPostCampaignReportListViewAmount
            backgroundColor={backgroundColor}
            testId="post-purchase-amount"
            pattern="!###,### "
            value={spend}
          />
        )}

        <RowWrapper data-testid="post-followers" backgroundColor={backgroundColor}>
          <Entry>
            <Text color={Theme.grey900Color}>{abbreviateNumber(followers)}</Text>
          </Entry>
        </RowWrapper>
        <AnalyticsPostCampaignReportListViewNumber
          backgroundColor={backgroundColor}
          testId="post-likes"
          value={likes}
        />
        <AnalyticsPostCampaignReportListViewNumber
          backgroundColor={backgroundColor}
          testId="post-comments"
          value={comments}
        />
        <AnalyticsPostCampaignReportListViewNumber
          backgroundColor={backgroundColor}
          testId="post-engagement"
          value={engagement}
        />
        <AnalyticsPostCampaignReportListViewPercentage
          backgroundColor={backgroundColor}
          testId="post-engagement-pct"
          value={engagementPct}
        />

        {!isPriceHidden && (
          <AnalyticsPostCampaignReportListViewAmount
            backgroundColor={backgroundColor}
            testId="post-cpe"
            value={cpe}
          />
        )}

        <AnalyticsPostCampaignReportListViewInsights
          testIdPrefix="post"
          backgroundColor={backgroundColor}
          reach={reach}
          reachPct={reachPct}
          impressions={impressions}
          cpm={cpm}
          isPriceHidden={isPriceHidden}
        />
      </Flex>
    </Container>
  )
}

export default AnalyticsPostCampaignReportPostListItem
